import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button, Form, Modal, Spinner} from 'react-bootstrap';
import {IconPlus, IconPencil, IconTrash, IconRobot, IconMenu2} from '@tabler/icons-react';
import {apiService} from '../services/api';
import {isClientAdmin} from '../utils/roles';
import {AppContext} from '../contexts/AppContext';
import {useSidebar} from '../contexts';
import {useAuth} from '../hooks/useAuth';
import '../pages/HomePage.css';

interface BotScreenLink {
    id?: string;
    screenId: string;
    screenName?: string;
    type: 'after_create' | 'after_update' | 'after_delete';
    typeLabel?: string;
}

interface AiBot {
    id: string;
    code: string;
    name: string;
    instructions: string;
    userId: number | null;
    userEmail: string | null;
    screens: BotScreenLink[];
    createdAt: string;
}

interface SystemUser {
    id: number;
    email: string;
}

interface ScreenOption {
    id: string;
    label: string;
}

const TYPE_LABELS: Record<BotScreenLink['type'], string> = {
    after_create: 'Po vytvorení',
    after_update: 'Po úprave',
    after_delete: 'Po vymazaní',
};

const emptyDraft = () => ({name: '', code: '', instructions: '', userId: 0, screens: [] as BotScreenLink[]});

/**
 * AI bots for client admins: which bot listens on which screen and after
 * what. The backend puts the chosen screens onto their tables' notify list
 * by itself, so nothing else has to be set up for the bot to run.
 */
export const AiBotsPage: React.FC = () => {
    const navigate = useNavigate();
    const {applications} = useContext(AppContext) ?? {applications: []};
    const {toggleSidebar} = useSidebar();
    const {user} = useAuth();

    const [bots, setBots] = useState<AiBot[]>([]);
    const [users, setUsers] = useState<SystemUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [editing, setEditing] = useState<AiBot | null | 'new'>(null);
    const [draft, setDraft] = useState(emptyDraft());
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState<AiBot | null>(null);

    useEffect(() => {
        if (!isClientAdmin()) {
            navigate('/', {replace: true});
        }
    }, [navigate]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const [botsRes, usersRes] = await Promise.all([apiService.getAiBots(), apiService.getAiBotSystemUsers()]);
        if (!botsRes.success) {
            setError(botsRes.message || 'AI botov sa nepodarilo načítať.');
        } else {
            setBots(botsRes.data?.data ?? []);
        }
        if (usersRes.success) {
            setUsers(usersRes.data?.data ?? []);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    /** Every screen the client knows, named with its application so same-named screens stay apart. */
    const screenOptions = useMemo<ScreenOption[]>(() => {
        const seen = new Map<string, ScreenOption>();
        for (const app of (applications ?? []) as any[]) {
            for (const screen of (app.screens ?? []) as any[]) {
                if (!screen?.id || seen.has(screen.id) || screen.style === 'statistic') continue;
                seen.set(screen.id, {id: screen.id, label: `${app.displayName || app.name} → ${screen.displayName}`});
            }
        }
        return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label, 'sk'));
    }, [applications]);

    const openNew = () => {
        setDraft({...emptyDraft(), userId: users[0]?.id ?? 0});
        setFieldErrors({});
        setEditing('new');
    };

    const openEdit = (bot: AiBot) => {
        setDraft({
            name: bot.name,
            code: bot.code,
            instructions: bot.instructions,
            userId: bot.userId ?? 0,
            screens: bot.screens.map(s => ({screenId: s.screenId, type: s.type})),
        });
        setFieldErrors({});
        setEditing(bot);
    };

    const close = () => {
        if (!saving) setEditing(null);
    };

    const save = async () => {
        setSaving(true);
        setFieldErrors({});
        const payload = {
            name: draft.name,
            code: draft.code,
            instructions: draft.instructions,
            userId: draft.userId,
            screens: draft.screens.filter(s => s.screenId).map(s => ({screenId: s.screenId, type: s.type})),
        };
        const res = editing === 'new'
            ? await apiService.createAiBot(payload)
            : await apiService.updateAiBot((editing as AiBot).id, payload);
        setSaving(false);
        if (!res.success) {
            const errs = res.errors;
            if (errs && !Array.isArray(errs)) {
                setFieldErrors(Object.fromEntries(Object.entries(errs).map(([k, v]) => [k, Array.isArray(v) ? v.join(' ') : String(v)])));
            } else {
                setFieldErrors({_: res.message || 'Uloženie zlyhalo.'});
            }
            return;
        }
        setEditing(null);
        await load();
    };

    const remove = async () => {
        if (!deleting) return;
        const res = await apiService.deleteAiBot(deleting.id);
        setDeleting(null);
        if (!res.success) {
            setError(res.message || 'Bota sa nepodarilo zmazať.');
            return;
        }
        await load();
    };

    const setScreen = (index: number, patch: Partial<BotScreenLink>) => {
        setDraft(d => ({...d, screens: d.screens.map((s, i) => (i === index ? {...s, ...patch} : s))}));
    };

    return (
        <>
            <div className="page-body">
                <div className="v5-topbar">
                    <button type="button" className="v5-topbar-toggle" onClick={toggleSidebar} aria-label="Prepnúť menu">
                        <IconMenu2 size={18}/>
                    </button>
                    <span className="start-topbar-label"><IconRobot size={15}/>AI boti</span>
                    {user?.tenantName && <span className="start-topbar-org">{user.tenantName}</span>}
                </div>
                <div className="container-fluid">
                    <section className="v5-display">
                        <div>
                            <h1>AI boti</h1>
                        </div>
                    </section>
                    <div className="v5-controls">
                        <div className="v5-controls-l"></div>
                        <div className="v5-controls-r">
                            <button type="button" className="btn btn-primary" onClick={openNew} disabled={loading}>
                                <IconPlus size={16} className="me-1" />Pridať
                            </button>
                        </div>
                    </div>

                    {error && <div className="alert alert-danger">{error}</div>}

                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    ) : bots.length === 0 ? (
                        <div className="card shadow-sm"><div className="card-body text-center text-muted py-5">
                            <IconRobot size={40} className="mb-2" />
                            <div>Zatiaľ žiadny AI bot. Bot dostane každú zmenu záznamu na vybraných obrazovkách a odpovie podľa svojich inštrukcií.</div>
                        </div></div>
                    ) : (
                        <div className="card shadow-sm">
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th>Bot</th>
                                            <th>Systémový účet</th>
                                            <th>Obrazovky</th>
                                            <th style={{width: 110}}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bots.map(bot => (
                                            <tr key={bot.id}>
                                                <td>
                                                    <div className="fw-semibold">{bot.name}</div>
                                                    <div className="text-muted small"><code>{bot.code}</code></div>
                                                </td>
                                                <td>{bot.userEmail ?? <span className="text-muted">{bot.userId === null ? 'bez účtu' : `#${bot.userId}`}</span>}</td>
                                                <td>
                                                    {bot.screens.length === 0 && <span className="text-muted">žiadne</span>}
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {bot.screens.map(s => (
                                                            <span key={s.id ?? `${s.screenId}-${s.type}`} className="badge bg-secondary-subtle text-secondary-emphasis border">
                                                                {s.screenName} · {s.typeLabel ?? TYPE_LABELS[s.type]}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="text-end text-nowrap">
                                                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(bot)} title="Upraviť">
                                                        <IconPencil size={15} />
                                                    </Button>
                                                    <Button size="sm" variant="outline-danger" onClick={() => setDeleting(bot)} title="Zmazať">
                                                        <IconTrash size={15} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Modal show={editing !== null} onHide={close} size="lg" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{editing === 'new' ? 'Nový AI bot' : 'Upraviť AI bota'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {fieldErrors._ && <div className="alert alert-danger">{fieldErrors._}</div>}
                    <div className="row g-3">
                        <div className="col-md-8">
                            <Form.Group>
                                <Form.Label>Názov</Form.Label>
                                <Form.Control
                                    value={draft.name}
                                    isInvalid={!!fieldErrors.name}
                                    onChange={e => setDraft(d => ({...d, name: e.target.value}))}
                                    placeholder="Kontrola príjemky"
                                />
                                <Form.Control.Feedback type="invalid">{fieldErrors.name}</Form.Control.Feedback>
                            </Form.Group>
                        </div>
                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Label>Kód</Form.Label>
                                <Form.Control
                                    value={draft.code}
                                    isInvalid={!!fieldErrors.code}
                                    onChange={e => setDraft(d => ({...d, code: e.target.value}))}
                                    placeholder="doplní sa z názvu"
                                />
                                <Form.Control.Feedback type="invalid">{fieldErrors.code}</Form.Control.Feedback>
                            </Form.Group>
                        </div>
                        <div className="col-12">
                            <Form.Group>
                                <Form.Label>Systémový účet</Form.Label>
                                <Form.Select
                                    value={draft.userId || ''}
                                    isInvalid={!!fieldErrors.userId}
                                    onChange={e => setDraft(d => ({...d, userId: Number(e.target.value)}))}
                                >
                                    <option value="">-- vyber účet --</option>
                                    {users.map(u => <option key={u.id} value={u.id}>{u.email}</option>)}
                                </Form.Select>
                                <Form.Text className="text-muted">Bot koná v mene tohto účtu, s jeho právami. Účet musí mať API kľúč, ten vydáva administrátor Flowy.</Form.Text>
                                <Form.Control.Feedback type="invalid">{fieldErrors.userId}</Form.Control.Feedback>
                            </Form.Group>
                        </div>
                        <div className="col-12">
                            <Form.Group>
                                <Form.Label>Inštrukcie</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={7}
                                    value={draft.instructions}
                                    isInvalid={!!fieldErrors.instructions}
                                    onChange={e => setDraft(d => ({...d, instructions: e.target.value}))}
                                    placeholder="Čo má bot urobiť, aké pravidlá dodržať a v akom tvare odpovedať."
                                />
                                <Form.Control.Feedback type="invalid">{fieldErrors.instructions}</Form.Control.Feedback>
                            </Form.Group>
                        </div>
                        <div className="col-12">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <Form.Label className="mb-0">Obrazovky a udalosti</Form.Label>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => setDraft(d => ({...d, screens: [...d.screens, {screenId: '', type: 'after_create'}]}))}
                                >
                                    <IconPlus size={14} className="me-1" />Pridať obrazovku
                                </Button>
                            </div>
                            {draft.screens.length === 0 && (
                                <div className="text-muted small">Bez obrazovky sa bot nikdy nespustí.</div>
                            )}
                            {draft.screens.map((link, index) => (
                                <div className="d-flex gap-2 mb-2" key={index}>
                                    <Form.Select value={link.screenId} onChange={e => setScreen(index, {screenId: e.target.value})}>
                                        <option value="">-- vyber obrazovku --</option>
                                        {screenOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                                    </Form.Select>
                                    <Form.Select
                                        style={{maxWidth: 180}}
                                        value={link.type}
                                        onChange={e => setScreen(index, {type: e.target.value as BotScreenLink['type']})}
                                    >
                                        {(Object.keys(TYPE_LABELS) as BotScreenLink['type'][]).map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                                    </Form.Select>
                                    <Button
                                        variant="outline-danger"
                                        onClick={() => setDraft(d => ({...d, screens: d.screens.filter((_, i) => i !== index)}))}
                                        title="Odobrať"
                                    >
                                        <IconTrash size={15} />
                                    </Button>
                                </div>
                            ))}
                            {fieldErrors.screens && <div className="text-danger small">{fieldErrors.screens}</div>}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={close} disabled={saving}>Zrušiť</Button>
                    <Button variant="primary" onClick={save} disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" className="me-1" /> : null}Uložiť
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={deleting !== null} onHide={() => setDeleting(null)}>
                <Modal.Header closeButton><Modal.Title>Zmazať bota</Modal.Title></Modal.Header>
                <Modal.Body>
                    Naozaj zmazať bota <strong>{deleting?.name}</strong>? Zmaže sa aj história jeho behov.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDeleting(null)}>Zrušiť</Button>
                    <Button variant="danger" onClick={remove}>Zmazať</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AiBotsPage;
